import React, { useState, useEffect } from 'react';
import { Bell, Check, Trash2, Filter, Ticket } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'sonner';

const NotificationCenter = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data);
    } catch (error) {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => 
        n.notification_id === id ? { ...n, is_read: true } : n
      ));
      toast.success('Marked as read');
    } catch (error) {
      // Update locally if API fails
      setNotifications(prev => prev.map(n => 
        n.notification_id === id ? { ...n, is_read: true } : n
      ));
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    }
  };

  const deleteNotification = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.notification_id !== id));
      toast.success('Notification deleted');
    } catch (error) {
      setNotifications(prev => prev.filter(n => n.notification_id !== id));
    }
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.is_read;
    if (filter === 'read') return n.is_read;
    return true;
  });

  const getTypeColor = (type) => {
    switch (type) {
      case 'booking': return 'text-green-400';
      case 'message': return 'text-blue-400';
      case 'payment': return 'text-yellow-400';
      case 'ticket_issued': return 'text-fuchsia-400';
      case 'event_reminder': return 'text-amber-400';
      default: return 'text-purple-400';
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative z-10 min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Bell className="w-8 h-8 text-purple-400" />
            <h1 className="text-3xl font-bold text-white">Notifications</h1>
          </div>
          <Button onClick={markAllAsRead} variant="outline" size="sm">
            <Check className="w-4 h-4 mr-2" />
            Mark All Read
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6">
          {['all', 'unread', 'read'].map(f => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              className="capitalize"
            >
              {f}
            </Button>
          ))}
        </div>

        {/* Notifications List */}
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="text-center py-12 bg-white/5 rounded-xl">
              <Bell className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">No notifications</p>
            </div>
          ) : (
            filteredNotifications.map(notification => (
              <div
                key={notification.notification_id}
                className={`p-4 rounded-xl ${notification.is_read ? 'bg-white/5' : 'bg-purple-500/20 border border-purple-500/30'}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className={`font-medium ${notification.is_read ? 'text-gray-300' : 'text-white'}`}>
                      {notification.message}
                    </p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className={`text-xs ${getTypeColor(notification.type)} capitalize`}>
                        {notification.type}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatDate(notification.created_at)}
                      </span>
                      {(notification.type === 'ticket_issued' || notification.type === 'event_reminder') && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-xs border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10"
                          onClick={() => {
                            if (!notification.is_read) markAsRead(notification.notification_id);
                            navigate('/events');
                          }}
                          data-testid={`view-ticket-${notification.notification_id}`}
                        >
                          <Ticket className="w-3 h-3 mr-1" />
                          View ticket
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {!notification.is_read && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => markAsRead(notification.notification_id)}
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteNotification(notification.notification_id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;